//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

import { Enums } from '../../models/common'
import { ColumnInfo } from '../../models/custom'
import { IReferenceType } from '../../models/util/interfaces'
import { OpDataType, OpLocation, OpMeter, OpView } from '../../models/core'

import { USER_SESSION_SERVICE_NAME, UserSessionService } from '../user-session'
import { Dictionary, LogHelper, TypeHelper } from '../../helpers'
import { KeyType } from '../../helpers/dictionary'
import { getEmptyObjectListResult } from '../../data-provider/utils'
import { DataProviderListResult } from '../../data-provider'


//#region Public Types

export const REFERENCE_TYPE_SERVICE_NAME = 'ReferenceTypeService'
export const REFERENCE_TYPE_SERVICE_PROVIDER_NAME = 'ReferenceTypeServiceProvider'

export type GetReferenceObjectListOptions = {
    filterOptions?: {
        columns: ColumnInfo[],
        customWhereClause: string
    },

    pagingOptions?: {
        pageNumber: number,
        pageSize: number,
    },

    contextIdDataType?: number,
    referenceValues?: any[],
}

//#endregion


export class ReferenceTypeService {

    //#region Consts

    private static get LOG_PREFIX(): string { return '[SIMAX.Services.ReferenceType]' }

    //#endregion

    //#region Ctor

    public constructor(private readonly userSessionService: UserSessionService) { }

    //#endregion

    //#region Get

    public getReferenceObjects<TReferenceType extends IReferenceType>(idReferenceType: Enums.ReferenceType, options?: GetReferenceObjectListOptions) {
        LogHelper.log(ReferenceTypeService.LOG_PREFIX, `Trying to load reference objects for reference type ${idReferenceType}.`)

        if (idReferenceType === Enums.ReferenceType.IdDataType) {
            // Loading data types using GetReferenceObjectList to return all of them.
            // Data types in dictionary in local storage can be limted to only necessary data types,
            // to reduce their size.
            return this.getDataTypeReferenceObjects(options);
        }

        return this
            .isViewReferenceType(idReferenceType)
            .then((hasView: boolean) => {
                if (hasView) {
                    return this
                        .getViewReferenceObjects(idReferenceType, options)
                        .then((result: DataProviderListResult<TReferenceType>) => {
                            LogHelper.log(ReferenceTypeService.LOG_PREFIX, `Loaded ${_.size(result.objects)} view objects from getReferenceObjectList.`)

                            return result
                        });
                }

                return this
                    .getDictionaryReferenceObjectList(idReferenceType)
                    .then((referenceObjects: TReferenceType[]) => {
                        if (_.size(referenceObjects) > 0) {
                            LogHelper.log(ReferenceTypeService.LOG_PREFIX, `Loaded ${_.size(referenceObjects)} objects from dictionary.`)

                            if (options != null) {
                                if (_.isArray(options.referenceValues)) {
                                    referenceObjects = _.filter(referenceObjects, (reference: TReferenceType) => _.contains(options.referenceValues, reference.getReferenceKey()))
                                }

                                if (options.pagingOptions != null) {
                                    return this.getPaginatedResult(referenceObjects, options.pagingOptions.pageNumber, options.pagingOptions.pageSize);
                                }
                            }

                            return this.getResult(referenceObjects);
                        }

                        return this
                            .getNonViewReferenceObjects(idReferenceType, options)
                            .then((result: DataProviderListResult<TReferenceType>) => {
                                LogHelper.log(ReferenceTypeService.LOG_PREFIX, `Loaded ${_.size(result.objects)} objects from getReferenceObjectList.`)

                                return result;
                            })
                    });
            });
    }

    private getResult<TReferenceType extends IReferenceType>(referenceObjects: TReferenceType[]): DataProviderListResult<TReferenceType> {
        const result: DataProviderListResult<TReferenceType> = getEmptyObjectListResult<TReferenceType>();

        result.objects = referenceObjects;
        result.totalCount = _.size(referenceObjects);

        return result
    }

    private getPaginatedResult<TReferenceType extends IReferenceType>(referenceObjects: TReferenceType[], pageNbr: number, pageSize: number): DataProviderListResult<TReferenceType> {
        const result: DataProviderListResult<TReferenceType> = getEmptyObjectListResult<TReferenceType>();
        const skipCount: number = (pageNbr - 1) * pageSize;
        const takeCount: number = pageSize;
        const paginatedObjects: TReferenceType[] = referenceObjects.slice(skipCount, skipCount + takeCount);

        result.objects = paginatedObjects;
        result.totalCount = _.size(referenceObjects);

        return result
    }

    private getDataTypeReferenceObjects<TReferenceType extends IReferenceType>(options: GetReferenceObjectListOptions): JQueryPromise<DataProviderListResult<TReferenceType>> {
        LogHelper.log(ReferenceTypeService.LOG_PREFIX, `Trying to load data type reference objects.`)

        return this
            .getNonViewReferenceObjects<TReferenceType>(Enums.ReferenceType.IdDataType, options)
            .then((result: DataProviderListResult<TReferenceType>) => {
                LogHelper.log(ReferenceTypeService.LOG_PREFIX, `Loaded ${_.size(result.objects)} data types from getReferenceObjectList.`)

                return result;
            })
    }

    private getViewReferenceObjects<TReferenceType extends IReferenceType>(idReferenceType: Enums.ReferenceType, options?: GetReferenceObjectListOptions): JQueryPromise<DataProviderListResult<TReferenceType>> {
        let pageSize: number = TypeHelper.MAX_INT_VALUE;
        let pageNbr: number = 1;
        let columns: ColumnInfo[] = [];
        let whereClause: string = "";
        let contextIdDataType: number = null;
        let referenceValues: any[] = null;

        if (options != null) {
            if (options.pagingOptions != null) {
                pageSize = options.pagingOptions.pageSize;
                pageNbr = options.pagingOptions.pageNumber;
            }

            if (options.filterOptions != null) {
                columns = options.filterOptions.columns;
                whereClause = options.filterOptions.customWhereClause;
            }

            referenceValues = options.referenceValues;
            contextIdDataType = options.contextIdDataType;
        }

        return this.userSessionService
            .getDataProvider()
            .System
            .getReferenceObjectList<TReferenceType>(
                idReferenceType,
                referenceValues,
                columns,
                pageNbr,
                pageSize,
                whereClause,
                contextIdDataType
            );
    }

    private getNonViewReferenceObjects<TReferenceType extends IReferenceType>(idReferenceType: Enums.ReferenceType, options: GetReferenceObjectListOptions): JQueryPromise<DataProviderListResult<TReferenceType>> {
        const referenceValues = options != null ? options.referenceValues : null;
        const contextIdDataType = options != null ? options.contextIdDataType : null;

        return this.userSessionService
            .getDataProvider().System
            .getReferenceObjectList<TReferenceType>(idReferenceType, referenceValues, null, 1, TypeHelper.MAX_INT_VALUE, '', contextIdDataType)
            .then((result: DataProviderListResult<TReferenceType>) => {
                if (options != null && options.pagingOptions != null) {
                    return this.getPaginatedResult(result.objects, options.pagingOptions.pageNumber, options.pagingOptions.pageSize);
                }

                return result;
            });
    }

    public getDictionaryReferenceObject<TReferenceType extends IReferenceType>(idReferenceType: Enums.ReferenceType, referenceValue: number): JQueryPromise<TReferenceType> {
        return this.getDictionaryReferenceObjectDict(idReferenceType).then((dict: Dictionary<TReferenceType>) => {
            if (dict != null) {
                return dict.tryGetValue(referenceValue);
            }

            return null;
        });
    }

    public getDictionaryReferenceObjectList<TReferenceType extends IReferenceType>(idReferenceType: Enums.ReferenceType): JQueryPromise<TReferenceType[]> {
        return this
            .getDictionaryReferenceObjectDict(idReferenceType)
            .then((referenceDict: Dictionary<TReferenceType>) => {
                if (referenceDict != null) {
                    return referenceDict.values() || []
                }

                return []
            })
    }

    public getDictionaryReferenceObjectDict<TReferenceType extends IReferenceType>(idReferenceType: Enums.ReferenceType): JQueryPromise<Dictionary<TReferenceType>> {
        return this.userSessionService
            .getDataProvider().System
            .getDictionaries([idReferenceType])
            .then((dicts: Dictionary<Dictionary<TReferenceType>>) => {
                const referenceDict = dicts.tryGetValue(idReferenceType);

                if (referenceDict != null) {
                    return referenceDict
                }

                return new Dictionary<TReferenceType>(null)
            })
            .fail((error: any) => {
                LogHelper.warn(ReferenceTypeService.LOG_PREFIX, 'Unable to load reference object list from dictionariy.', error)

                return new Dictionary<TReferenceType>(null)
            })
    }

    //#endregion

    //#region Save

    public saveReferenceObject(referenceObject: IReferenceType): JQueryPromise<IReferenceType> {
        if (referenceObject == null) {
            return $.Deferred<IReferenceType>().resolve(null).promise()
        }

        const referenceType: Enums.ReferenceType = referenceObject.getReferenceType()

        switch (referenceType) {
            case Enums.ReferenceType.IdLocation: {
                return this.userSessionService.getDataProvider().Location.saveLocation(<OpLocation>referenceObject)
            }

            case Enums.ReferenceType.IdMeter: {
                return this.userSessionService.getDataProvider().Meter.saveMeter(<OpMeter>referenceObject)
            }

            default: {
                LogHelper.error(ReferenceTypeService.LOG_PREFIX, `Unsupported reference type: ${Enums.ReferenceType[referenceType]}`)
                return null
            }
        }
    }

    //#endregion

    //#region Utils

    public extractReferenceTypeDictionary<T extends IReferenceType>(dictionaries: Dictionary<Dictionary<IReferenceType>>, referenceType: Enums.ReferenceType, keyProvider: (item: T) => (string | number)): Dictionary<T> {
        let items: T[] = []

        if (dictionaries != null && dictionaries.containsKey(referenceType)) {
            const dict: Dictionary<IReferenceType> = dictionaries.tryGetValue(referenceType)

            if (dict != null && dict.values != null) {
                const referenceTypeObjects: IReferenceType[] = dict.values()

                if (referenceTypeObjects.length > 0) {
                    items = referenceTypeObjects.map((item: IReferenceType) => <T>item)
                }
            }
        }

        return Dictionary.createFromArray(items, KeyType.Number, keyProvider)
    }

    public isViewReferenceType(idReferenceType: Enums.ReferenceType) {
        return this.userSessionService
            .getDataProvider().View
            .getActiveView(idReferenceType)
            .then((view: OpView) => {
                return view != null;
            });
    }

    public isViewReferenceTypes(idReferenceTypes: ReadonlyArray<Enums.ReferenceType>): JQueryPromise<Dictionary<boolean>> {
        const resultDict: Dictionary<boolean> = new Dictionary<boolean>(null, KeyType.Number);
        const nonNullReferenceTypes = _.filter(idReferenceTypes, idReferenceType => idReferenceType != null)

        const viewPromises = _.map(nonNullReferenceTypes, idReferenceType => {
            return this.isViewReferenceType(idReferenceType);
        });

        return $.when(...viewPromises).then((...result: boolean[]) => {
            _.forEach(result, (isView: boolean, index: number) => {
                resultDict.add(nonNullReferenceTypes[index], isView);
            })

            return resultDict;
        });
    }

    //#endregion

}

export class ReferenceTypeServiceProvider implements ng.IServiceProvider {

    //#region IServiceProvider Implementation 

    public $get = [USER_SESSION_SERVICE_NAME, function (userSessionService: UserSessionService) {
        return new ReferenceTypeService(userSessionService)
    }]

    //#endregion
} 